import {
  Box, Grid, Paper, Typography, Stack, Avatar, Chip,
  LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Divider,
} from '@mui/material';
import { keyframes } from '@mui/system';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(20px); }
  to   { opacity:1; transform:translateY(0); }
`;

const StatCard = ({ icon, label, value, sub, color, delay }) => (
  <Paper sx={{
    p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,.03)',
    border: `1px solid ${color}22`, position: 'relative', overflow: 'hidden',
    animation: `${fadeUp} .6s ease forwards`, animationDelay: delay, opacity: 0,
    transition: 'all .3s',
    '&:hover': { border: `1px solid ${color}55`, transform: 'translateY(-4px)' },
  }}>
    <Box sx={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', bgcolor: `${color}10` }} />
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: 1.5 }}>{label}</Typography>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.1, mt: .5 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: color, mt: .5, display: 'block' }}>{sub}</Typography>
      </Box>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15` }}>
        <Box sx={{ color }}>{icon}</Box>
      </Box>
    </Stack>
  </Paper>
);

const STATS = [
  { icon: <PeopleOutlineIcon />,       label: 'Joueurs Actifs',   value: '248',  sub: '+12 ce mois',     color: '#D4AF37', delay: '0s'   },
  { icon: <EmojiEventsOutlinedIcon />, label: 'Tournois Actifs',  value: '14',   sub: '3 en cours',      color: '#22C55E', delay: '.1s'  },
  { icon: <TrendingUpIcon />,          label: 'Évaluations',      value: '1 203', sub: 'Cette saison',   color: '#3B82F6', delay: '.2s'  },
  { icon: <SportsSoccerIcon />,        label: 'Matchs Joués',     value: '89',   sub: '↑ 18% vs saison', color: '#F97316', delay: '.3s'  },
];

const TOP_PLAYERS = [
  { name: 'Mamadou Diallo',  pos: 'Attaquant',      rating: 87, progress: 87 },
  { name: 'Ibrahim Konaté',  pos: 'Milieu Central', rating: 85, progress: 85 },
  { name: 'Oumar Baldé',     pos: 'Défenseur',      rating: 83, progress: 83 },
  { name: 'Lansana Camara',  pos: 'Gardien',        rating: 80, progress: 80 },
];

const ACTIVITY = [
  { text: 'Mamadou Diallo évalué par Coach Sylla',   time: 'Il y a 2h',  color: '#D4AF37' },
  { text: 'Tournoi U21 — phase de groupes lancée',   time: 'Il y a 4h',  color: '#22C55E' },
  { text: 'Nouvel utilisateur : Ibrahim Bah (joueur)', time: 'Hier',      color: '#3B82F6' },
  { text: 'Rapport mensuel généré automatiquement',  time: 'Hier',       color: '#F97316' },
  { text: 'Entité Hafia FC mise à jour',             time: 'Il y a 2j',  color: '#D4AF37' },
];

const AdminDashboard = ({ user }) => (
  <Box>
    {/* Welcome */}
    <Box sx={{ mb: 4, animation: `${fadeUp} .5s ease forwards`, opacity: 0 }}>
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900 }}>
        Bonjour, {user.firstName} 👋
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.4)', mt: .5 }}>
        Vue administrateur · Voici le résumé de la plateforme aujourd'hui.
      </Typography>
    </Box>

    {/* Stat cards */}
    <Grid container spacing={3} mb={4}>
      {STATS.map((s, i) => (
        <Grid item xs={12} sm={6} xl={3} key={i}>
          <StatCard {...s} />
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3}>
      {/* Top players */}
      <Grid item xs={12} lg={6}>
        <Paper sx={{
          p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(212,175,55,.12)',
          animation: `${fadeUp} .6s ease forwards`, animationDelay: '.35s', opacity: 0,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>Top Talents</Typography>
            <Chip label="Cette saison" size="small" sx={{ bgcolor: 'rgba(212,175,55,.1)', color: '#D4AF37', fontSize: '.7rem' }} />
          </Stack>
          {TOP_PLAYERS.map((p, i) => (
            <Box key={i} mb={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={.8}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(212,175,55,.15)', color: '#D4AF37', fontSize: '.8rem', fontWeight: 700 }}>
                    {p.name[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.2 }}>{p.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.35)' }}>{p.pos}</Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" sx={{ color: '#D4AF37', fontWeight: 800 }}>{p.rating}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={p.progress} sx={{
                height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,.06)',
                '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 2 },
              }} />
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Activity feed */}
      <Grid item xs={12} lg={6}>
        <Paper sx={{
          p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(212,175,55,.12)',
          animation: `${fadeUp} .6s ease forwards`, animationDelay: '.45s', opacity: 0,
        }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 3 }}>Activité Récente</Typography>
          <List disablePadding>
            {ACTIVITY.map((a, i) => (
              <Box key={i}>
                <ListItem disableGutters sx={{ py: 1.2 }}>
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 10, color: a.color, mt: .5 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={a.text}
                    secondary={a.time}
                    primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,.7)', fontSize: '.85rem' } }}
                    secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,.3)', fontSize: '.75rem' } }}
                  />
                </ListItem>
                {i < ACTIVITY.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,.05)' }} />}
              </Box>
            ))}
          </List>
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

export default AdminDashboard;
